import { QueryObserverResult, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  getAllocationOfCostsTaskDetails,
  getCostByInitiativeTaskDetails,
  getCostVsOutputTaskDetails,
} from '../../api/financials-client/financials-client';
import type { MinifiedTask } from '../../api/tasks-client/task-client.type';
import { useGlobalStore } from '../../store/global-store/global-store';
import type { FinancialsTasksScope } from './financials-tasks.type';

/**
 * Query manager for fetching cost vs output financials tasks.
 *
 * @param {FinancialsTasksConfig} config - Configuration object for fetching financials tasks
 * @param {UseQueryOptions<MinifiedTask[]>} options - Options for the useQuery hook
 * @return {{ data: MinifiedTask[] | undefined; query: QueryObserverResult<MinifiedTask[]> }} Object containing data and query result
 */
const useCostVsOutputTasks = (
  { portfolioId, projectId, subprojectId, workPeriodId, date, factor, startDate, endDate }: FinancialsTasksScope,
  options?: UseQueryOptions<MinifiedTask[]>,
): { data: MinifiedTask[] | undefined; query: QueryObserverResult<MinifiedTask[]> } => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [
      'financials-tasks',
      portfolioId,
      projectId,
      subprojectId,
      workPeriodId,
      date,
      factor,
      startDate,
      endDate,
      organizationId,
    ],
    queryFn: () =>
      portfolioId && startDate && endDate && date && factor && organizationId
        ? getCostVsOutputTaskDetails(
            portfolioId,
            startDate,
            endDate,
            date,
            factor,
            projectId || undefined,
            subprojectId || undefined,
            workPeriodId || undefined,
            organizationId,
          )
        : Promise.reject('Cannot get cost vs output tasks'),
    enabled: Boolean(portfolioId && startDate && endDate && date && factor && organizationId),
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Query manager for fetching allocation of costs financials tasks.
 *
 * @param {FinancialsTasksScope} config - Configuration object for fetching financials tasks
 * @param {UseQueryOptions<MinifiedTask[]>} options - Options for the useQuery hook
 * @return {{ data: MinifiedTask[] | undefined; query: QueryObserverResult<MinifiedTask[]> }} Object containing data and query result
 */
const useAllocationOfCostsTasks = (
  {
    portfolioId,
    projectId,
    subprojectId,
    workPeriodId,
    date,
    factor,
    factorOption,
    startDate,
    endDate,
  }: FinancialsTasksScope,
  options?: UseQueryOptions<MinifiedTask[]>,
): { data: MinifiedTask[] | undefined; query: QueryObserverResult<MinifiedTask[]> } => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: [
      'financials-tasks',
      portfolioId,
      projectId,
      subprojectId,
      workPeriodId,
      date,
      factor,
      factorOption,
      startDate,
      endDate,
      organizationId,
    ],
    queryFn: () =>
      portfolioId && startDate && endDate && date && factor && factorOption && organizationId
        ? getAllocationOfCostsTaskDetails(
            portfolioId,
            startDate,
            endDate,
            date,
            factor,
            factorOption,
            projectId || undefined,
            subprojectId || undefined,
            workPeriodId || undefined,
            organizationId,
          )
        : Promise.reject('Cannot get cost vs output tasks'),
    enabled: Boolean(portfolioId && startDate && endDate && date && factor && factorOption && organizationId),
    ...options,
  });

  return { data: query.data, query };
};

/**
 * Query manager for fetching cost by initiative financials tasks.
 *
 * @param {FinancialsTasksScope} config - Configuration object for fetching financials tasks
 * @param {UseQueryOptions<MinifiedTask[]>} options - Options for the useQuery hook
 * @return {{ data: MinifiedTask[] | undefined; query: QueryObserverResult<MinifiedTask[]> }} Object containing data and query result
 */

const useCostByInitiativeTasks = (
  { portfolioId, projectId, workPeriodId, factor, startDate, endDate }: FinancialsTasksScope,
  options?: UseQueryOptions<MinifiedTask[]>,
): { data: MinifiedTask[] | undefined; query: QueryObserverResult<MinifiedTask[]> } => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const query = useQuery({
    queryKey: ['financials-tasks', portfolioId, projectId, workPeriodId, factor, startDate, endDate, organizationId],
    queryFn: () =>
      portfolioId && startDate && endDate && factor && organizationId
        ? getCostByInitiativeTaskDetails(
            portfolioId,
            startDate,
            endDate,
            factor,
            projectId || undefined,
            workPeriodId || undefined,
            organizationId,
          )
        : Promise.reject('Cannot get cost by initiative tasks'),
    enabled: Boolean(portfolioId && startDate && endDate && factor && organizationId),
    ...options,
  });

  return { data: query.data, query };
};

export { useAllocationOfCostsTasks, useCostByInitiativeTasks, useCostVsOutputTasks };
